import { forwardRef } from "react";
import { NavLink as RouterNavLink, NavLinkProps as RouterNavLinkProps } from "react-router-dom";

import { PrefetchConfig, PrefetchHandler } from "../../domain/_shared/preloader/components/prefetch-handler";

import { useUnsafeNavigationControls } from "./use-navigation-control-context";

interface NavLinkProps extends RouterNavLinkProps {
  prefetchConfig?: Omit<PrefetchConfig, "to">;
  replace?: boolean;
}

/**
 * Custom NavLink component that extends React Router's NavLink with prefetching capability
 * and navigation tracking
 * @param props - Component props including standard NavLink props and prefetching options
 * @param ref - Forwarded ref from parent component
 */
export const NavLink = forwardRef<HTMLAnchorElement, NavLinkProps>(
  ({ prefetchConfig, replace = false, onClick, ...props }, ref) => {
    const maybeNavigationControls = useUnsafeNavigationControls();

    const handleClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      // Call the original onClick if provided
      if (onClick) {
        onClick(e);
      }

      // If the event was prevented by the original handler or is not a simple left click,
      // don't do anything extra
      if (
        e.defaultPrevented ||
        e.button !== 0 ||
        e.metaKey ||
        e.altKey ||
        e.ctrlKey ||
        e.shiftKey ||
        props.target === "_blank"
      ) {
        return;
      }

      // Track the navigation event
      maybeNavigationControls?.trackNavigation(props.to.toString(), { replace });

      // Let the native component handle the navigation
      // No need to prevent default or manually navigate
    };

    return (
      <PrefetchHandler
        prefetchConfig={{ onMouseEnter: props.onMouseEnter, ...prefetchConfig, to: props.to.toString() }}
      >
        <RouterNavLink ref={ref} {...props} replace={replace} onClick={handleClick} />
      </PrefetchHandler>
    );
  },
);

NavLink.displayName = "NavLink";
